import { QuestionNameInput } from "./question-name";
import { Button, Card, Form, FormListFieldData, Popconfirm, Select, Space } from "antd";
import { CopyOutlined, DeleteOutlined } from "@ant-design/icons";
import { IntelligenceFormFieldsEnum } from "../../enum";
import { JsonQueryBuilderMemo } from "../json-query/query-builder";
import React from "react";
import { useQueryDependenciesGraphContext } from "../../context/questions-context";
import { SmlQueryBuilderProvider } from "../../context/sml-query-builder-context";
import { StoreValue } from "antd/es/form/interface";
import { generateQuestionElementId } from "../../list";
import dayjs from "dayjs";
import { TimeRangeInput } from "./time-range";
import { FormItemLayoutWrapper } from "../form-item-layout-wrapper";
import { css } from "@emotion/react";
import { SmlAsset, SmlJsonQueryFilter } from "@src/types/intelligence-search";
import { QuestionDependenciesWrapper } from "./question-dependencies-wrapper";
import { PERMISSION_LAYOUT_CHECK } from "@src/components/crud-permission-layout";

const QuestionCartExtra: React.FC<{
    formListFieldData: FormListFieldData;
    add: any;
    remove: any;
}> = ({ formListFieldData, add, remove }) => {
    const { name, key } = formListFieldData;
    const form = Form.useFormInstance();
    const { removeQuery, isPredecessor, setQueryAssetMeta } = useQueryDependenciesGraphContext();
    const queryKey = key.toString();

    const questionWatch = Form.useWatch(IntelligenceFormFieldsEnum.QUESTIONS, form);
    const questionWatchLength = questionWatch?.length ?? 0;

    return (
        <div className={`flex items-center`}>
            <Popconfirm
                title="Do you want to duplicate this question?"
                onConfirm={() => {
                    const questionDuplicate = form.getFieldValue([IntelligenceFormFieldsEnum.QUESTIONS, name]);
                    const {
                        [IntelligenceFormFieldsEnum.QUESTION_ID]: _,
                        [IntelligenceFormFieldsEnum.QUESTION_NAME]: questionName,
                        ...restQuestionDuplicate
                    } = questionDuplicate;
                    const newQuestionValue = {
                        [IntelligenceFormFieldsEnum.QUESTION_NAME]: questionName + " copy",
                        ...restQuestionDuplicate,
                    };

                    add(newQuestionValue);
                }}
            >
                <Button type="text" icon={<CopyOutlined className="text-gray-500" />} />
            </Popconfirm>
            {questionWatchLength > 1 && (
                <Popconfirm
                    title="Do you want to remove this question?"
                    onConfirm={() => {
                        const currentQuestions: any[] = form.getFieldValue(IntelligenceFormFieldsEnum.QUESTIONS) ?? [];
                        const newQuestions = currentQuestions.filter((_, index) => index !== name);

                        removeQuery(queryKey);
                        setQueryAssetMeta((prev) => {
                            const { [queryKey]: _, ...rest } = prev;
                            return rest;
                        });

                        remove(name);
                        setTimeout(() => {
                            form.setFieldValue(IntelligenceFormFieldsEnum.QUESTIONS, newQuestions);
                        }, 100);
                    }}
                >
                    <Button danger type="text" disabled={isPredecessor(queryKey)} icon={<DeleteOutlined />} />
                </Popconfirm>
            )}

            {/* <a href="#">More</a> */}
        </div>
    );
};

export const QuestionBuilderForm: React.FC<{
    value?: any;
    formListFieldData: FormListFieldData;
    productCode?: string;
    platform?: string[];
    add: (defaultValue?: StoreValue, insertIndex?: number) => void;
    remove: (index: number | number[]) => void;
    isHighlighted?: boolean;
    onChange?: any;
}> = ({ value, formListFieldData, productCode, platform, add, remove, isHighlighted, onChange }) => {
    const { name, key, ...restField } = formListFieldData;
    const form = Form.useFormInstance();
    const queryKey = key.toString();
    const { upsertQuery, generateJsonQuery, setQueryAssetMeta } = useQueryDependenciesGraphContext();

    const {
        [IntelligenceFormFieldsEnum.TIME_RANGE]: timeRange,
        [IntelligenceFormFieldsEnum.QUESTION_NAME]: questionName,
        [IntelligenceFormFieldsEnum.JSON_QUERY]: jsonQuery,
        [IntelligenceFormFieldsEnum.QUERY_DEPENDENCIES]: queriesDependencies,
        [IntelligenceFormFieldsEnum.QUESTION_ID]: questionId,
        [IntelligenceFormFieldsEnum.JSON_QUERY_BUILDER_CONFIG]: jsonQueryBuilderConfig,
    } = value || {};
    React.useEffect(() => {
        if (!value) return;
        if (
            value?.[IntelligenceFormFieldsEnum.QUESTION_ID] === null ||
            value?.[IntelligenceFormFieldsEnum.QUESTION_ID] === undefined
        ) {
            return;
        }

        upsertQuery(queryKey, name, questionName, jsonQuery, queriesDependencies);
        const selectedAssetsOrFilters: SmlJsonQueryFilter[] = jsonQueryBuilderConfig?.selected_assets_or_filters ?? [];

        setQueryAssetMeta((prev) => ({
            ...prev,
            [queryKey]: selectedAssetsOrFilters.reduce((acc, curr) => {
                return {
                    ...acc,
                    [curr.name]: curr?.meta,
                };
            }, {} as Record<string, SmlAsset | undefined>),
        }));
    }, [value]);

    React.useEffect(() => {
        if (!questionId) {
            onChange({
                ...form.getFieldValue([IntelligenceFormFieldsEnum.QUESTIONS, name]),
                [IntelligenceFormFieldsEnum.QUESTION_ID]: key,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [key, name, questionId]);

    return (
        <SmlQueryBuilderProvider
            key={key}
            queryKey={queryKey}
            productCode={productCode}
            rawJsonQuery={jsonQuery}
            timeRange={timeRange}
            getGeneratedJsonQuery={(jsonQueryArg) => {
                return generateJsonQuery(
                    queryKey,
                    form.getFieldValue([
                        IntelligenceFormFieldsEnum.QUESTIONS,
                        name,
                        IntelligenceFormFieldsEnum.QUERY_DEPENDENCIES_TYPE,
                    ]),
                    jsonQueryArg,
                );
            }}
        >
            <Card
                css={css`
                    &.parent-card > .ant-card-head {
                        background-color: rgba(233, 246, 254, 0.4) !important;
                        border-color: #1890ff !important;
                        border-style: solid !important;
                        border-width: 1px !important;
                    }
                    &.parent-card > .ant-card-body {
                        border-color: #1890ff !important;
                        border-style: solid !important;
                        border-width: 1px !important;
                    }
                `}
                id={generateQuestionElementId(questionId)}
                size="small"
                className={`${PERMISSION_LAYOUT_CHECK} ${
                    isHighlighted ? "question-card-highlighted parent-card" : "parent-card"
                }`}
                title={
                    <Form.Item
                        className="m-4"
                        {...restField}
                        initialValue={`Question ${key + 1}`}
                        name={[name, IntelligenceFormFieldsEnum.QUESTION_NAME]}
                        rules={[{ required: true, message: "Question name" }]}
                    >
                        <QuestionNameInput />
                    </Form.Item>
                }
                extra={<QuestionCartExtra add={add} remove={remove} formListFieldData={formListFieldData} />}
            >
                <Form.Item {...restField} name={[name, IntelligenceFormFieldsEnum.QUESTION_ID]} hidden />
                <div className="flex flex-wrap pr-10">
                    <FormItemLayoutWrapper className="">
                        <Form.Item
                            className="mr-12"
                            label={""}
                            name={[name, IntelligenceFormFieldsEnum.TIME_RANGE]}
                            initialValue={[dayjs().add(-15, "d"), dayjs().add(-1, "d")]}
                            required
                            rules={[
                                {
                                    message: "Please select time range",
                                    required: true,
                                },
                            ]}
                        >
                            <TimeRangeInput />
                        </Form.Item>
                    </FormItemLayoutWrapper>
                    <QuestionDependenciesWrapper name={name} fieldKey={key} queryKey={queryKey} />
                </div>
                <FormItemLayoutWrapper>
                    <Form.Item
                        label={""}
                        layout="horizontal"
                        {...restField}
                        name={[name, IntelligenceFormFieldsEnum.JSON_QUERY_BUILDER_CONFIG]}
                    >
                        <JsonQueryBuilderMemo
                            formListFieldData={formListFieldData}
                            form={form}
                            queryKey={queryKey}
                            productCode={productCode}
                            platform={platform}
                            timeRange={form.getFieldValue([
                                IntelligenceFormFieldsEnum.QUESTIONS,
                                name,
                                IntelligenceFormFieldsEnum.TIME_RANGE,
                            ])}
                            onChange={(query, builderConfig) => {
                                form.setFieldValue(
                                    [
                                        IntelligenceFormFieldsEnum.QUESTIONS,
                                        name,
                                        IntelligenceFormFieldsEnum.JSON_QUERY_BUILDER_CONFIG,
                                    ],
                                    builderConfig,
                                );

                                form.setFieldValue(
                                    [IntelligenceFormFieldsEnum.QUESTIONS, name, IntelligenceFormFieldsEnum.JSON_QUERY],
                                    query,
                                );
                            }}
                        />
                    </Form.Item>
                </FormItemLayoutWrapper>

                <Form.Item name={[name, IntelligenceFormFieldsEnum.JSON_QUERY]} hidden />
            </Card>
        </SmlQueryBuilderProvider>
    );
};

export const QuestionBuilderFormMemo = React.memo(QuestionBuilderForm, (prev, curr) => {
    const { add: _1, remove: _2, ...rest1 } = prev;

    const { add: _3, remove: _4, ...rest2 } = curr;

    return JSON.stringify(rest1) === JSON.stringify(rest2);
});
